'use client';

import type { Person, Relationship } from '@/lib/types';
import { useProfile } from './ProfileContext';
import { applyRelation, relationOf, replaceRelationId } from './relations';
import { Chip, Kicker, PageBody } from './ui';

/** The gender-aware label for a relationship as seen from the other side. */
export function relationshipLabel(r: Relationship, genderId: number | null): string {
  return (genderId === 1 ? r.maleLabel : genderId === 2 ? r.femaleLabel : null) ?? r.name;
}

/**
 * Who `other` is to `subject` (both in the household), grouped by
 * Relationship_Types. One tap writes both MP rows; picking a different one
 * ends the old row first. `subject` is me from the "To you" flow and any
 * household member from their Family card.
 */
export function RelationshipPage({
  subject,
  other,
  onDone,
}: {
  subject: Person;
  other: Person;
  onDone: () => void;
}) {
  const { data, api, run, update } = useProfile();
  const isMe = subject.contactId === data.me.contactId;
  const current = relationOf(subject, other.contactId);
  const subjectName = isMe ? 'your' : `${subject.nickname || subject.firstName}'s`;

  const choose = (r: Relationship) => {
    if (current?.relationshipId === r.id) return;
    const snapshot = data;
    const tempId = -Date.now();
    update((d) => applyRelation(d, subject, other, r, tempId));
    onDone();
    void run(
      async () => {
        if (current) await api.removeRelationship(current.contactRelationshipId);
        const saved = await api.addRelationship(
          other.contactId,
          r.id,
          isMe ? undefined : subject.contactId
        );
        update((d) => replaceRelationId(d, tempId, saved.contactRelationshipId));
      },
      () => update(() => snapshot)
    );
  };

  const clear = () => {
    if (!current) return;
    const snapshot = data;
    update((d) => applyRelation(d, subject, other, null, 0));
    onDone();
    void run(
      () => api.removeRelationship(current.contactRelationshipId),
      () => update(() => snapshot)
    );
  };

  return (
    <PageBody>
      <p className="pii text-sm text-gray-600">
        {other.nickname || other.firstName} is {subjectName}…
      </p>
      {[...data.lookups.relationshipTypes]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((t) => (
          <div key={t.id} className="flex flex-col gap-2">
            <Kicker>{t.name}</Kicker>
            <div className="flex flex-wrap gap-2">
              {t.relationships.map((r) => (
                <Chip
                  key={r.id}
                  active={current?.relationshipId === r.id}
                  onClick={() => choose(r)}
                >
                  {relationshipLabel(r, other.genderId)}
                </Chip>
              ))}
            </div>
          </div>
        ))}
      {current && (
        <button
          type="button"
          onClick={clear}
          className="self-start text-sm font-semibold text-gray-500 hover:text-red-600"
        >
          Remove this relationship
        </button>
      )}
    </PageBody>
  );
}
